import { Breadcrumb, Button, Col, Input, Modal, Row, Table } from "antd";
import React, { useEffect, useState } from "react";
import { AnyObject } from "../../lib/interfaces";
import {
  ICreateRoutingItem,
  ICreateRoutingPlan,
  IRoutingPlan,
  IRoutingPlanDetails,
  IRoutingPlanVersion,
  IRoutingPlanVersionsList,
  ISupplierOrrering,
  ISupplierOrreringSettings,
  IUpdateRoutingItem,
  IUpdateRoutingPlan,
} from "../../store/slices/routingPlanes/types/routingPlanes";
import { expandedTableEyeIcon } from "../../lib/icons";
import { useAppDispatch } from "../..";
import {
  fetchCreatePlane,
  fetchRoutingPlane,
  fetchRoutingPlanesVersions,
  fetchSupplierOfferings,
  fetchUpdatePlane,
} from "../../store/slices/routingPlanes/routingPlanes";
import { ReactComponent as SupplierOfferinsAdd } from "../../img/supplierOfferingsAdd.svg";
import { ReactComponent as SupplierOfferinsDetails } from "../../img/supplierOfferingsDetails.svg";
import { ReactComponent as ArrowMoveToUp } from "../../img/arrowMoveToUp.svg";
import { ReactComponent as ArrowMoveToDown } from "../../img/arrowMoveToDown.svg";
import { ReactComponent as DeleteIcon } from "../../img/delete.svg";
import {
  mapRoutingPlan,
  mapSupplierOffering,
} from "../../store/slices/routingPlanes/mappers/fromDto";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import { ReactComponent as ArrowDetailsIcon } from "../../img/arrow-details.svg";

interface IProps {
  columns: AnyObject[];
  columnDetail: AnyObject[];
  getRowClassName: (record: IRoutingPlan, idx) => string;
  openPlanToEdit: IRoutingPlan | undefined;
  dataSourceVersions: IRoutingPlanVersionsList[];
  getVersionsRowClassName: (record: IRoutingPlanVersion, idc: number) => string;
  tcOfferingItemId: number | undefined;
  destinationId: number | undefined;
  currencyId: number | undefined;
  dicCurrencies: AnyObject[];
  handleBack: () => void;
  destinationCategories: AnyObject[];
  setObjectVersionHistory: (record: IRoutingPlanVersion) => void;
}

interface ICreatedOfferAttributes {
  id: number;
  orderStatusId: number;
  fromDate: string;
  toDate: string;
}

const supplierOfferingColumnWidth = {
  organization: "25%",
  offeringName: "",
  cost: "",
  datePeriod: "20%",
  rateNext: "10%",
  rateMax: "10%",
  rateMin: "10%",
  buttons: "5%",
};

const columnRoutingOrdersWidth = {
  destination: "20%",
  abbrev: "10%",
  offeringName: "15%",
  categoryId: "15%",
  orderStatus: "10%",
  datePeriod: "",
  buttons: "50px",
};

export const RoutingPlanSetting = ({
  columns,
  getRowClassName,
  openPlanToEdit,
  columnDetail,
  dataSourceVersions,
  getVersionsRowClassName,
  tcOfferingItemId,
  destinationId,
  currencyId,
  dicCurrencies,
  handleBack,
  destinationCategories,
  setObjectVersionHistory,
}: IProps) => {
  const dispatch = useAppDispatch();

  const [openVersion, setOpenVersion] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<IRoutingPlan[]>([]);
  const [loadingPlane, setLoadingPlane] = useState<boolean>(true);
  const [loadingSupplierOffering, setLoadingSupplierOffering] = useState<boolean>(true);
  const [modalSave, setModalSave] = useState<boolean>(false);
  const [modalSend, setModalSend] = useState<boolean>(false);
  const [isSavedOffer, setIsSavedOffer] = useState<boolean>(false);
  const [orderResponseAttributes, setOrderResponseAttributes] = useState<
    ICreatedOfferAttributes | undefined
  >(undefined);
  const [dataSourceVersionList, setDataSourceVersionList] = useState<IRoutingPlanVersion[]>([]);

  const handleToggleDetails = (dataIndex, record: IRoutingPlanVersion) => {
    setDataSourceVersionList((prev) => {
      return prev.map((item) => {
        if (item.id === record.id) {
          item.isOpenDetails = !item.isOpenDetails;
        }
        return item;
      });
    });
  };

  useEffect(() => {
    dataSourceVersions.forEach((version) => {
      if (version.id === tcOfferingItemId) {
        const list = version.list.map((item) => {
          if (Array.isArray(item.routingItems)) {
            const routingItems = item.routingItems.map((elem) => {
              const currencyName = getCurrencyNameById(dicCurrencies, elem.currencyId);
              return { ...elem, currencyName };
            });
            item.routingItems = routingItems;
          }
          return item;
        });
        setDataSourceVersionList(list);
      }
    });
  }, [dataSourceVersions]);

  const [columnVersions, setColumnVersions] = useState([
    {
      title: "Направление",
      dataIndex: "destination",
      key: "destination",
      hidden: false,
      width: columnRoutingOrdersWidth.destination,
      render: (dataIndex, record: IRoutingPlanVersion) => {
        return (
          <div style={{ display: "flex" }}>
            <span style={{ paddingRight: "5px" }}>{record.destination}</span>
            <span>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleDetails(dataIndex, record);
                }}
                className={
                  record.isOpenDetails
                    ? "button-icon iconButton routingOrder-icon routingOrder-icon__rotate"
                    : "button-icon iconButton routingOrder-icon"
                }
                style={{ outline: "none", cursor: "pointer" }}
                icon={<ArrowDetailsIcon />}
              />
            </span>
          </div>
        );
      },
    },
    {
      title: "Аббревиатура",
      key: "abbrev",
      dataIndex: "abbrev",
      hidden: true,
      width: columnRoutingOrdersWidth.abbrev,
    },
    {
      title: "Продукт",
      key: "offeringName",
      dataIndex: "offeringName",
      hidden: false,
      width: columnRoutingOrdersWidth.offeringName,
    },
    {
      title: "Категория",
      key: "categoryId",
      dataIndex: "categoryId",
      hidden: false,
      width: columnRoutingOrdersWidth.categoryId,
      render: (_, record) => {
        let category = "";
        Array.isArray(destinationCategories) &&
          destinationCategories.forEach((item) => {
            if (item.id === record.categoryId) {
              category = item.name;
            }
          });
        return category;
      },
    },
    {
      title: "Период",
      key: "datePeriod",
      dataIndex: "datePeriod",
      hidden: false,
      width: columnRoutingOrdersWidth.datePeriod,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnRoutingOrdersWidth.buttons,
      className: "history-column",
      style: { display: "flex", justifyContent: "end" },
      hidden: false,
      render: (value, record: IRoutingPlanVersion) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            setObjectVersionHistory(record);
          }}
          className="iconButton"
          type="text"
          htmlType="button"
          icon={<DotsIcon />}
        />
      ),
    },
  ]);

  useEffect(() => {
    setColumnVersions((prev) => {
      return prev.map((item) => {
        columns.forEach((column) => {
          if (item.dataIndex === column.dataIndex) {
            item.hidden = column.hidden || false;
          }
        });
        return item;
      });
    });
  }, [columns]);

  const [columnSupplierOfferings, setColumnSupplierOfferings] = useState<AnyObject[]>([
    {
      title: "Оператор",
      key: "organization",
      dataIndex: "organization",
      width: supplierOfferingColumnWidth.organization,
    },
    {
      title: "Продукт",
      key: "offeringName",
      dataIndex: "offeringName",
      render: (dataIndex, record: ISupplierOrrering) => {
        return (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{}}>{record?.offeringName}</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {getSupplierOfferingCountRender(record) && (
                <div>
                  <SupplierOfferinsDetails />
                </div>
              )}
              <div
                style={{
                  color: "#00BB77",
                  paddingLeft: 5,
                  visibility: getSupplierOfferingCountRender(record) ? "visible" : "hidden",
                }}
              >
                {`+${record.toDestinationsCount}`}
              </div>
            </div>
          </div>
        );
      },
      width: supplierOfferingColumnWidth.offeringName,
    },
    {
      title: "Цена",
      key: "cost",
      dataIndex: "cost",
      width: supplierOfferingColumnWidth.cost,
    },
    {
      title: "Период",
      key: "datePeriod",
      dataIndex: "datePeriod",
      width: supplierOfferingColumnWidth.datePeriod,
    },
    {
      title: "Next",
      key: "rateNext",
      dataIndex: "rateNext",
      width: supplierOfferingColumnWidth.rateNext,
    },
    {
      title: "Max",
      key: "rateMax",
      dataIndex: "rateMax",
      width: supplierOfferingColumnWidth.rateMax,
    },
    {
      title: "Min",
      key: "rateMin",
      dataIndex: "rateMin",
      width: supplierOfferingColumnWidth.rateMin,
    },
    {
      title: "",
      key: "buttons",
      dataIndex: "buttons",
      render: (dataIndex, record) => {
        return (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleAddOffering(dataIndex, record);
            }}
            style={{ outline: "none", cursor: "pointer", border: "none" }}
            icon={<SupplierOfferinsAdd />}
          />
        );
      },
      width: supplierOfferingColumnWidth.buttons,
    },
  ]);

  const [dataSourceSupplierOffering, setDataSourceSupplierOffering] = useState<
    ISupplierOrreringSettings[]
  >([]);

  const normalizeOfferings = (offeringsSetting: ISupplierOrreringSettings[]) => {
    console.log("offeringsSetting", offeringsSetting);

    let currentGroupList: AnyObject[] = [];
    const resultDublicateIdxList: number[] = [];

    let sortList = offeringsSetting.sort((a, b) => a.key - b.key);

    sortList = sortList.map((item) => {
      if (offeringsSetting.length === item.key + 1) {
        item.isLast = true;
      } else {
        item.isLast = false;
      }
      return item;
    });

    sortList = sortList.map((item, idx) => {
      let isRowValid = true;
      if (idx === 0 && !item.proportionRatio) {
        isRowValid = false;
      }
      return { ...item, isRowValid };
    });

    sortList.forEach((item, idx) => {
      if (item.proportionRatio) {
        const allIds: number[] = [];
        const dublicateIds: number[] = [];
        currentGroupList.forEach((elem) => {
          if (allIds.includes(elem.id)) {
            dublicateIds.push(elem.id);
          } else {
            allIds.push(elem.id);
          }
        });

        currentGroupList.forEach((elem) => {
          if (dublicateIds.includes(elem.id)) {
            resultDublicateIdxList.push(elem.idx);
          }
        });

        currentGroupList = [];
        currentGroupList.push({ idx, id: item.id });
      } else {
        currentGroupList.push({ idx, id: item.id });
      }
    });

    if (currentGroupList.length) {
      const allIds: number[] = [];
      const dublicateIds: number[] = [];
      currentGroupList.forEach((elem) => {
        if (allIds.includes(elem.id)) {
          dublicateIds.push(elem.id);
        } else {
          allIds.push(elem.id);
        }
      });
      currentGroupList.forEach((elem) => {
        if (dublicateIds.includes(elem.id)) {
          resultDublicateIdxList.push(elem.idx);
        }
      });
    }
    sortList = sortList.map((item, idx) => {
      if (resultDublicateIdxList.includes(idx)) {
        item.isRowValid = false;
      }
      return item;
    });

    console.log("sortList", sortList);

    return sortList;
  };

  const getValidPropportionRatio = (record: ISupplierOrreringSettings) => {
    if (
      record.proportionRatio &&
      Number(record.proportionRatio) > 0 &&
      Number(record.proportionRatio) <= 100
    ) {
      return true;
    }
    return false;
  };

  const [columnsOfferingSettings, setColumnsOfferingSettings] = useState<AnyObject[]>([
    {
      title: "%",
      key: "proportionRatio",
      dataIndex: "proportionRatio",
      render: (dataIndex, record: ISupplierOrreringSettings) => {
        const handleChangeProportionRatio = (value: string, record: ISupplierOrreringSettings) => {
          setDataSourceOfferingSettings((prev) => {
            const newLias = prev.map((item) => {
              if (item.key === record.key) {
                item.proportionRatio = value;
              }
              return item;
            });
            let count = 0;
            newLias.forEach((item) => {
              if (item.proportionRatio) {
                count += Number(item.proportionRatio);
              }
            });
            return normalizeOfferings(
              newLias.map((item) => {
                return {
                  ...item,
                  isValidProportionRatio: count <= 100 && getValidPropportionRatio(item),
                };
              })
            );
          });
        };

        return (
          <Input
            type="number"
            style={{
              height: 20,
              width: 50,
              borderColor:
                record.proportionRatio && !record.isValidProportionRatio ? "red" : "#BBB9B9",
            }}
            value={record.proportionRatio}
            onChange={(e) => handleChangeProportionRatio(e.target.value, record)}
          />
        );
      },
    },
    {
      title: "Поставщик",
      key: "organization",
      dataIndex: "organization",
    },
    {
      title: "Продукт",
      key: "offeringName",
      dataIndex: "offeringName",
    },
    {
      title: "Цена",
      key: "cost",
      dataIndex: "cost",
    },
    {
      title: "Валюта",
      key: "currencyName",
      dataIndex: "currencyName",
    },
    {
      title: "",
      key: "actions",
      dataIndex: "actions",
      render: (dataIndex, record: ISupplierOrreringSettings) => {
        const handleMoveToUp = (record: ISupplierOrreringSettings) => {
          setDataSourceOfferingSettings((prev) => {
            const sortedOffering: ISupplierOrreringSettings[] = [];
            prev.forEach((item) => {
              let newKey = item.key;
              if (record.key === item.key) {
                newKey--;
              } else if (record.key - 1 === item.key) {
                newKey++;
              }
              sortedOffering.push({
                ...item,
                key: newKey,
              });
            });
            return normalizeOfferings(sortedOffering);
          });
        };

        const handleMoveToDown = (record: ISupplierOrreringSettings) => {
          setDataSourceOfferingSettings((prev) => {
            const sortedOffering: ISupplierOrreringSettings[] = [];
            prev.forEach((item) => {
              let newKey = item.key;
              if (record.key === item.key) {
                newKey++;
              } else if (record.key + 1 === item.key) {
                newKey--;
              }
              sortedOffering.push({
                ...item,
                key: newKey,
              });
            });
            return normalizeOfferings(sortedOffering);
          });
        };

        return (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleMoveToUp(record);
              }}
              style={{
                outline: "none",
                cursor: "pointer",
                border: "none",
                visibility: record.key > 0 ? "visible" : "hidden",
                backgroundColor: "inherit",
              }}
              icon={<ArrowMoveToUp />}
            />
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleMoveToDown(record);
              }}
              style={{
                outline: "none",
                cursor: "pointer",
                border: "none",
                visibility: record.isLast ? "hidden" : "visible",
                backgroundColor: "inherit",
              }}
              icon={<ArrowMoveToDown />}
            />
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteOfferingSettingsItem(dataIndex, record);
              }}
              style={{
                outline: "none",
                cursor: "pointer",
                border: "none",
                backgroundColor: "inherit",
              }}
              icon={<DeleteIcon />}
            />
          </div>
        );
      },
    },
  ]);

  const getSupplierOfferingCountRender = (record: ISupplierOrrering) => {
    if (record.toDestinationsCount > 1) {
      return true;
    }
    return false;
  };

  const handleDeleteOfferingSettingsItem = (dataIndex, record: ISupplierOrrering) => {
    setDataSourceOfferingSettings((prev) => {
      const filterOfferings: ISupplierOrreringSettings[] = prev.filter(
        (item) => item.key !== record.key
      );
      return filterOfferings.map((item, idx) => {
        return {
          ...item,
          key: idx,
          isLast: idx + 1 === filterOfferings.length,
        };
      });
    });
  };

  const [dataSourceOfferingSettings, setDataSourceOfferingSettings] = useState<
    ISupplierOrreringSettings[]
  >([]);

  useEffect(() => {
    if (isSavedOffer) {
      setIsSavedOffer(false);
    }
  }, [dataSourceOfferingSettings]);

  const handleAddOffering = (dataIndex, record) => {
    setDataSourceOfferingSettings((prev) => {
      const newArr = [...prev];
      newArr.push({
        ...record,
        key: 0,
        isLast: true,
      });
      return normalizeOfferings(
        newArr.map((item, idx) => {
          if (newArr.length === idx + 1) {
            item.isLast = true;
          } else {
            item.isLast = false;
          }
          item.key = idx;
          return item;
        })
      );
    });
  };

  const getCurrencyNameById = (dicCurrencies: AnyObject[], id: number) => {
    let name = "";
    Array.isArray(dicCurrencies) &&
      dicCurrencies.forEach((item) => {
        if (item.id === id) {
          name = item.name;
        }
      });
    return name;
  };

  useEffect(() => {
    if (tcOfferingItemId) {
      setLoadingPlane(true);
      dispatch(fetchRoutingPlane({ tcOfferingItemId, currencyId })).then((response) => {
        const data: IRoutingPlan = mapRoutingPlan(response.payload);
        data.routingItems = data.routingItems.map((item) => {
          return {
            ...item,
            currencyName: getCurrencyNameById(dicCurrencies, item.currencyId),
          };
        });
        data.isOpenDetails = true;
        setDataSource([data]);
        setLoadingPlane(false);
      });
    }
  }, [tcOfferingItemId]);

  useEffect(() => {
    if (destinationId && currencyId) {
      setLoadingSupplierOffering(true);
      dispatch(fetchSupplierOfferings({ destinationId, currencyId })).then((response) => {
        const data: ISupplierOrrering[] = mapSupplierOffering(response.payload);
        const offering: ISupplierOrreringSettings[] = data.map((item, idx) => {
          return {
            ...item,
            proportionRatio: "",
            isLast: idx + 1 === data.length,
            isValidProportionRatio: true,
            isRowValid: true,
          };
        });
        setDataSourceSupplierOffering(offering);
        setLoadingSupplierOffering(false);
      });
    }
  }, [destinationId, currencyId]);

  const getDetailsTableData = (record: IRoutingPlan): IRoutingPlanDetails[] => {
    return record.routingItems;
  };

  const getExpandedRowKeys = (dataSource: IRoutingPlan[]) => {
    const keys: number[] = [];
    dataSource.forEach((item) => {
      keys.push(item.key);
    });
    return keys;
  };

  const expanderVersionsRowRender = (record: any) => {
    return (
      <Table
        columns={columnDetail}
        dataSource={getDetailsTableData(record)}
        pagination={false}
        className="table_no-hover child-table"
      />
    );
  };

  const getExpandedVersionRowKeys = (dataSourceVersionList: IRoutingPlanVersion[]) => {
    const idxList: number[] = [];
    dataSourceVersionList.forEach((item) => {
      if (item.isOpenDetails) {
        idxList.push(item.key);
      }
    });
    return idxList;
  };

  const expandedRowRender = (record: IRoutingPlan) => {
    return (
      <>
        <Table
          columns={columnDetail}
          dataSource={getDetailsTableData(record)}
          pagination={false}
          className="table_no-hover child-table"
        />
        {openVersion && (
          <Table
            columns={[...columnVersions].filter((item) => !item.hidden)}
            dataSource={dataSourceVersionList}
            rowClassName={getVersionsRowClassName}
            pagination={false}
            showHeader={false}
            className="table_no-hover child-table"
            expandable={{
              expandedRowKeys: getExpandedVersionRowKeys(dataSourceVersionList),
              expandedRowRender: expanderVersionsRowRender,
              expandIconColumnIndex: 6,
              columnWidth: "0px",
              indentSize: 0,
              expandIcon: () => {
                return undefined;
              },
            }}
          />
        )}
      </>
    );
  };

  const handleToggleVersion = async (record: IRoutingPlan) => {
    if (!openVersion) {
      await dispatch(
        fetchRoutingPlanesVersions({
          tcOfferingItemId: record.tcOfferingItemId,
        })
      ).then((response) => {
        setOpenVersion((prev) => {
          return !prev;
        });
      });
    } else {
      setOpenVersion((prev) => {
        return !prev;
      });
    }
  };

  const getValidSave = (dataSourceOfferingSettings: ISupplierOrreringSettings[]) => {
    let valid = true;
    if (dataSourceOfferingSettings.length === 0) {
      valid = false;
    }
    if (valid && !dataSourceOfferingSettings[0].proportionRatio) {
      valid = false;
    }
    let summaryProportionRatio = 0;
    dataSourceOfferingSettings.forEach((item) => {
      if (item.proportionRatio) {
        summaryProportionRatio += Number(item.proportionRatio);
      }
    });
    if (summaryProportionRatio !== 100) {
      valid = false;
    }
    return valid;
  };

  const handleSaveOffer = (dataSourceOfferingSettings: ISupplierOrreringSettings[]) => {
    if (tcOfferingItemId && destinationId) {
      let proportionGroup = 0;
      let proportionRatio = 0;
      let sequence = 0;
      const routingItems: ICreateRoutingItem[] = dataSourceOfferingSettings.map((item) => {
        if (item.proportionRatio) {
          proportionGroup++;
          proportionRatio = Number(item.proportionRatio);
          sequence = 0;
        }
        sequence++;
        return {
          sequence,
          proportionGroup,
          proportionRatio,
          toOfferingId: item.id,
        };
      });

      const body: ICreateRoutingPlan = {
        tcOfferingItemId,
        tcDestinationId: destinationId,
        routingItems,
      };

      dispatch(fetchCreatePlane(body)).then((response) => {
        if (response.payload) {
          setOrderResponseAttributes({
            id: response.payload?.id,
            orderStatusId: response.payload?.orderStatusId,
            fromDate: response.payload?.fromDate,
            toDate: response.payload?.toDate,
          });
          setIsSavedOffer(true);
        }
        setModalSave(false);
      });
    }
  };

  const handleSendOffer = (dataSourceOfferingSettings: ISupplierOrreringSettings[]) => {
    if (tcOfferingItemId && destinationId && orderResponseAttributes) {
      const body: IUpdateRoutingPlan = {
        id: orderResponseAttributes.id,
        tcOfferingItemId,
        tcDestinationId: destinationId,
        orderStatusId: 2,
        routingItems: [],
      };
      dispatch(fetchUpdatePlane(body)).then((response) => {
        if (response.payload) {
          setOrderResponseAttributes(undefined);
          setDataSourceOfferingSettings([]);
          setIsSavedOffer(false);
        }
        setModalSend(false);
      });
    }
  };

  const getSupplierOfferingClassName = (record: ISupplierOrreringSettings) => {
    const className: string[] = [];
    if (record.key > 0 && !!record.proportionRatio) {
      className.push("routingPlans-create__firstRow");
    }
    if (!record.isRowValid) {
      className.push("routingPlans-create__unvalidRow");
    }

    return className.join(" ");
  };

  const handleClickBack = (e) => {
    e.preventDefault();
    handleBack();
  };

  const getColumns = (columns) => {
    return columns.map((item) => {
      const _item = { ...item };
      if (item.dataIndex === "destination") {
        _item.render = undefined;
      }
      return _item;
    });
  };

  return (
    <>
      <div className="routingPlans">
        <Breadcrumb className="breadcrumb">
          <Breadcrumb.Item>
            <a onClick={handleClickBack}>План маршрутизации</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Настройка плана маршрутизации</Breadcrumb.Item>
        </Breadcrumb>
        <Row justify="space-between" align="middle">
          <h1 className="main-title">Настройка плана маршрутизации</h1>
        </Row>

        <div className="routingPlans-table versions-table">
          <Row style={{ border: "1px solid #BBB9B9", marginBottom: 10 }}>
            <Table
              size="small"
              className="table_no-hover"
              columns={[...getColumns(columns)].filter((item) => !item.hidden)}
              dataSource={dataSource}
              loading={loadingPlane}
              pagination={false}
              scroll={{ x: 1200 }}
              rowClassName={(row, idx) => getRowClassName(row, idx)}
              expandable={{
                expandedRowKeys: getExpandedRowKeys(openPlanToEdit ? [openPlanToEdit] : []),
                expandedRowRender,
                expandIconColumnIndex: [...columns].filter((item) => !item.hidden).length - 1,
                columnWidth: "50px",
                indentSize: 0,
                expandIcon: ({ expanded, record }) => {
                  return (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleVersion(record);
                        }}
                        className="button-icon iconButton"
                        style={{ outline: "none", cursor: "pointer" }}
                        icon={expandedTableEyeIcon(record.isOpenVersions ? "#0E5298" : "#86ACD2")}
                      />
                    </div>
                  );
                },
              }}
            />
          </Row>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ width: "55%", minHeight: 200, border: "1px solid #BBB9B9" }}>
              <Table
                columns={columnSupplierOfferings}
                dataSource={dataSourceSupplierOffering}
                pagination={false}
                className="table_no-hover child-table"
                loading={loadingSupplierOffering}
                // style={{marginLeft: '-8px', marginRight: '-8px'}}
              />
            </div>
            <div style={{ width: "44%", minHeight: 200, border: "1px solid #BBB9B9" }}>
              <Table
                columns={columnsOfferingSettings}
                dataSource={dataSourceOfferingSettings}
                pagination={false}
                className="table_no-hover child-table"
                rowClassName={getSupplierOfferingClassName}
                // style={{marginLeft: '-8px', marginRight: '-8px'}}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: 300,
                  margin: "15px auto",
                }}
              >
                <Button onClick={() => setDataSourceOfferingSettings([])}>Очистить</Button>
                <Button
                  disabled={!getValidSave(dataSourceOfferingSettings) || isSavedOffer}
                  onClick={() => setModalSave(true)}
                >
                  Сохранить
                </Button>
                <Button type="primary" disabled={!isSavedOffer} onClick={() => setModalSend(true)}>
                  Отправить
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Сохранить заказ?"
        visible={modalSave}
        onCancel={() => setModalSave(false)}
        centered
        footer={[
          <Button key="1" onClick={() => setModalSave(false)}>
            Нет
          </Button>,
          <Button
            key="2"
            type="primary"
            onClick={() => handleSaveOffer(dataSourceOfferingSettings)}
          >
            Да
          </Button>,
        ]}
      />

      <Modal
        title="Отправить заказ в обработку?"
        visible={modalSend}
        onCancel={() => setModalSend(false)}
        centered
        footer={[
          <Button key="1" onClick={() => setModalSend(false)}>
            Нет
          </Button>,
          <Button
            key="2"
            type="primary"
            onClick={() => handleSendOffer(dataSourceOfferingSettings)}
          >
            Да
          </Button>,
        ]}
      />
    </>
  );
};
